import { useState, useEffect, useMemo } from 'react';
import api from '../api';
import {
  Receipt,
  RefreshCw,
  Search,
  X,
  Filter,
  User,
  Gift,
  Shield,
  Clock,
  CheckCircle,
  Zap,
  UserCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function RedemptionLog() {
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [stats, setStats] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [redemptionsRes, statsRes] = await Promise.all([
        api.get('/admin/rewards/log/redemptions', { params: { limit: 500 } }),
        api.get('/admin/rewards/stats/redemptions').catch(() => ({ data: {} })),
      ]);
      
      setRedemptions(redemptionsRes.data.redemptions || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching redemption data:', error);
      toast.error('Failed to load redemption data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredRedemptions = useMemo(() => {
    let filtered = redemptions;

    // Apply method filter
    if (methodFilter !== 'all') {
      filtered = filtered.filter(r => r.redemptionMethod === methodFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => {
        const userName = r.user?.name?.toLowerCase() || '';
        const userEmail = r.user?.email?.toLowerCase() || '';
        const rewardTitle = r.reward?.title?.toLowerCase() || '';
        const staffName = r.staffName?.toLowerCase() || '';
        const staffId = r.staffId?.toLowerCase() || '';
        
        return (
          userName.includes(query) ||
          userEmail.includes(query) ||
          rewardTitle.includes(query) ||
          staffName.includes(query) ||
          staffId.includes(query)
        );
      });
    }

    return filtered;
  }, [redemptions, searchQuery, methodFilter]);

  const getMethodLabel = (method) => {
    switch (method) {
      case 'staff_auth': return 'Staff Verified';
      case 'self_service': return 'Self Service';
      case 'auto': return 'Auto';
      default: return method;
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'staff_auth': return <Shield className="w-4 h-4" />;
      case 'self_service': return <UserCheck className="w-4 h-4" />;
      case 'auto': return <Zap className="w-4 h-4" />;
      default: return <CheckCircle className="w-4 h-4" />;
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'staff_auth': return 'text-green-400 bg-green-500/20';
      case 'self_service': return 'text-blue-400 bg-blue-500/20';
      case 'auto': return 'text-purple-400 bg-purple-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Redemption Log</h1>
          <p className="text-gray-400 mt-1">Track all reward redemptions</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Receipt className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.totalRedemptions || 0}</div>
                <div className="text-sm text-gray-400">Total Redemptions</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.staffVerified || 0}</div>
                <div className="text-sm text-gray-400">Staff Verified</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.selfService || 0}</div>
                <div className="text-sm text-gray-400">Self Service</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">${stats.totalValue || 0}</div>
                <div className="text-sm text-gray-400">Total Value</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by user, reward, or staff..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Method Filter */}
        <div className="flex items-center gap-2">
          <Filter size={20} className="text-gray-400" />
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
          >
            <option value="all">All Methods</option>
            <option value="staff_auth">Staff Verified</option>
            <option value="self_service">Self Service</option>
            <option value="auto">Auto</option>
          </select>
        </div>
      </div>

      {/* Results Count */}
      {(searchQuery || methodFilter !== 'all') && (
        <div className="text-sm text-gray-400">
          Showing {filteredRedemptions.length} of {redemptions.length} redemptions
          {(searchQuery || methodFilter !== 'all') && (
            <button
              onClick={() => { setSearchQuery(''); setMethodFilter('all'); }}
              className="ml-2 text-amber-500 hover:text-amber-400"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">User</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Reward</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Method</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Verified By</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {filteredRedemptions.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No redemptions found</p>
                  {(searchQuery || methodFilter !== 'all') && (
                    <button
                      onClick={() => { setSearchQuery(''); setMethodFilter('all'); }}
                      className="mt-2 text-amber-500 hover:text-amber-400"
                    >
                      Clear filters
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              filteredRedemptions.map((redemption) => (
                <tr key={redemption.id} className="hover:bg-gray-750">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                      <div>
                        <div className="font-medium text-white">
                          {redemption.user?.name || 'Unknown User'}
                        </div>
                        <div className="text-sm text-gray-400">
                          {redemption.user?.email || '—'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Gift className="w-4 h-4 text-amber-500" />
                      <span className="text-white">
                        {redemption.reward?.title || 'Unknown Reward'}
                      </span>
                      {redemption.reward?.value && (
                        <span className="text-green-400 text-sm">
                          (${redemption.reward.value})
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm ${getMethodColor(redemption.redemptionMethod)}`}>
                      {getMethodIcon(redemption.redemptionMethod)}
                      {getMethodLabel(redemption.redemptionMethod)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {redemption.staffName ? (
                      <div className="flex items-center gap-2 text-green-400">
                        <Shield className="w-4 h-4" />
                        <span>{redemption.staffName}</span>
                        {redemption.staffId && (
                          <span className="text-gray-500">({redemption.staffId})</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Clock className="w-4 h-4" />
                      {formatDate(redemption.redeemedAt)}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
